import { List, Button, Divider, Popconfirm } from "antd";
import {
  PlusOutlined,
  SwapOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useState, useEffect, Fragment } from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";
import { findIndex } from "ramda";
import NewWidget from "./new";
import EditWidget from "./edit";

export default function WidgetList({
  data = [],
  widgets,
  onChange,
  lang,
  ...props
}) {
  const [modalNewOpen, setModalNewOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (data?.[lang]?.widgets?.length > 0) {
      setItems(data?.[lang]?.widgets || []);
    }
  }, [data?.[lang]?.widgets]);

  useEffect(() => {
    onChange({
      ...data,
      [lang]: {
        ...data?.[lang],
        widgets: items,
      },
    });
  }, [items]);

  const triggerModalNew = () => setModalNewOpen(!modalNewOpen);
  const triggerModalEdit = (data = null) => setModalEdit(data || null);

  const onSortEnd = (oldIndex, newIndex) => {
    setItems((array) => arrayMoveImmutable(array, oldIndex, newIndex));
  };

  const onItemRemove = (key) => {
    const newItems = items.filter((item, index) => index !== key);
    setItems(newItems);
  };

  return (
    <Fragment>
      <SortableList
        onSortEnd={onSortEnd}
        className="list"
        draggedItemClassName="dragged"
      >
        <List
          dataSource={items}
          renderItem={(item, key) => (
            <SortableItem key={key}>
              <List.Item className="p-0 list-none">
                <div className="w-full grid grid-cols-12 gap-4 py-3">
                  <div className="col-span-12 lg:col-span-8">
                    <span className="block text-base w-full text-black font-medium">
                      {item?.name}
                    </span>
                  </div>
                  <div className="col-span-12 lg:col-span-4 flex justify-end">
                    <div className="flex items-center">
                      <SwapOutlined
                        className="text-neutral-400 cursor-grab hover:text-neutral-700"
                        style={{ rotate: "90deg" }}
                      />
                      <Divider type="vertical" />
                      <Button
                        icon={<EditOutlined />}
                        type="link"
                        className="m-0 p-0 h-fit w-fit"
                        onClick={() => triggerModalEdit(item)}
                      />
                      <Divider type="vertical" />
                      <Popconfirm
                        onConfirm={() => onItemRemove(key)}
                        okText="Delete"
                        title="Delete content widget?"
                      >
                        <Button
                          danger
                          type="link"
                          className="m-0 p-0 h-fit w-fit"
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              </List.Item>
            </SortableItem>
          )}
          footer={
            <Button
              disabled={items.length >= 50}
              onClick={triggerModalNew}
              block
              icon={<PlusOutlined />}
              type="dashed"
            >
              Add Content Widget
            </Button>
          }
        />
      </SortableList>
      <NewWidget
        {...props}
        open={modalNewOpen}
        onCancel={triggerModalNew}
        widgets={widgets}
        onAfterAdd={(item) => {
          setItems([...items, { ...item, id: Date.now() }]);
          triggerModalNew();
        }}
      />
      <EditWidget
        {...props}
        item={modalEdit}
        onCancel={(e) => {
          e.preventDefault();
          triggerModalEdit();
        }}
        widgets={widgets}
        onAfterEdit={(item) => {
          const idx = findIndex((i) => i.id === item.id, items);
          const newItems = [...items];
          newItems[idx] = item;
          setItems([...newItems]);
          triggerModalEdit();
        }}
      />
    </Fragment>
  );
}
